import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { FiCheck, FiClock, FiShield } from "react-icons/fi";
import { useAuthProvider } from "../Authentication/AuthProviderSwitch";
import { getPublicSubscriptionPlans } from "../../services/publicSubscriptionsApi";
import {
  createClientSubscriptionCheckoutSession,
  setPaymentsApiTokenProvider,
} from "../../services/paymentsApi";
import { entitlementLabels, fallbackTiers } from "../Subscribe/tiersConfig";
import { CLIENT_FLOW_STORAGE_KEYS, safeParseJson } from "./clientFlowUtils";

const coreServiceOptions = [
  "Case / Legal Management as a Service",
  "Case Management as a Service",
  "Contract Management as a Service",
  "Property Management as a Service",
];

const formatCurrency = (amount, currency = "USD") => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "Custom";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const fallbackLookup = (() => {
  const map = new Map();
  fallbackTiers.forEach((tier) => {
    if (tier.id !== undefined) map.set(String(tier.id), tier);
    if (tier.tier !== undefined) map.set(`tier:${tier.tier}`, tier);
    [tier.name, tier.label].filter(Boolean).forEach((value) => {
      map.set(String(value).toLowerCase(), tier);
    });
  });
  return map;
})();

const getFallbackPlan = (plan) => {
  const candidateId = plan.id ?? plan.planId ?? plan.key ?? plan.tier;
  if (candidateId !== undefined) {
    const directMatch =
      fallbackLookup.get(String(candidateId)) || fallbackLookup.get(`tier:${candidateId}`);
    if (directMatch) return directMatch;
  }
  const nameKey = String(plan.name ?? plan.label ?? "").toLowerCase();
  return nameKey ? fallbackLookup.get(nameKey) || null : null;
};

const normalizePlan = (plan, fallback = {}) => {
  const tier =
    plan.tier ??
    plan.tierLevel ??
    plan.level ??
    plan.planTier ??
    fallback.tier;
  const id = plan.id ?? plan.planId ?? plan.key ?? tier ?? fallback.id;
  const name =
    plan.name ??
    plan.title ??
    fallback.name ??
    (tier ? `Telivi Tier ${tier}` : "Telivi Tier");
  const label = plan.label ?? plan.nickname ?? plan.subtitle ?? fallback.label ?? name;
  return {
    ...fallback,
    ...plan,
    id,
    tier,
    name,
    label,
    monthlyPrice:
      plan.monthlyPrice ??
      plan.monthly_amount ??
      plan.amount ??
      plan.price ??
      plan.monthlyRate ??
      fallback.monthlyPrice ??
      null,
    setupFee: plan.setupFee ?? plan.setup_fee ?? plan.setupAmount ?? fallback.setupFee ?? null,
    currency: plan.currency ?? plan.currencyCode ?? fallback.currency ?? "USD",
    recommended: Boolean(plan.recommended ?? plan.isRecommended ?? fallback.recommended ?? tier === 2),
    turnaroundDays:
      plan.turnaroundDays ??
      plan.turnaround_days ??
      plan.turnaround ??
      plan.turnaroundBusinessDays ??
      fallback.turnaroundDays ??
      null,
    servicesIncluded: plan.servicesIncluded ?? fallback.servicesIncluded ?? null,
    dashboardsIncluded: plan.dashboardsIncluded ?? fallback.dashboardsIncluded ?? null,
    platformGuarantees: plan.platformGuarantees ?? fallback.platformGuarantees ?? null,
    limits: plan.limits ?? fallback.limits ?? null,
    entitlements: plan.entitlements ?? plan.entitlementFlags ?? fallback.entitlements ?? null,
    marketingDescription: plan.marketingDescription ?? fallback.marketingDescription ?? "",
    description: plan.description ?? fallback.description ?? "",
  };
};

const normalizeIntervalUnit = (value) => String(value || "").trim().toUpperCase();

const isRecurringPlan = (plan) => {
  if (typeof plan.isRecurring === "boolean") return plan.isRecurring;
  if (typeof plan.recurring === "boolean") return plan.recurring;
  const intervalUnit = normalizeIntervalUnit(plan.intervalUnit ?? plan.interval ?? plan.interval_unit);
  if (intervalUnit) return intervalUnit !== "ONE_TIME" && intervalUnit !== "ONETIME";
  const type = normalizeIntervalUnit(plan.type ?? plan.priceType ?? plan.billingType);
  return type ? type === "RECURRING" : true;
};

const getPlanGroupKey = (plan) => {
  if (plan.label) return String(plan.label).trim().toLowerCase();
  if (plan.name) return String(plan.name).trim().toLowerCase();
  if (plan.tier !== undefined && plan.tier !== null) return `tier:${plan.tier}`;
  if (plan.id !== undefined && plan.id !== null) return `id:${plan.id}`;
  return null;
};

const scorePlan = (plan) => {
  let score = 0;
  const intervalUnit = normalizeIntervalUnit(plan.intervalUnit ?? plan.interval ?? plan.interval_unit);
  if (intervalUnit === "MONTH") score += 4;
  if (intervalUnit) score += 1;
  if (Number(plan.intervalCount) === 1) score += 1;
  if (plan.isRecurring === true || plan.recurring === true) score += 2;
  if (Number(plan.monthlyPrice) > 0) score += 1;
  const name = String(plan.name || plan.label || "").toLowerCase();
  const description = String(plan.description || "").toLowerCase();
  if (name.includes("setup") || description.includes("setup")) score -= 4;
  return score;
};

const dedupePlans = (planList) => {
  const grouped = new Map();
  planList.forEach((plan) => {
    const key = getPlanGroupKey(plan);
    if (!key) return;
    const current = grouped.get(key);
    if (!current || scorePlan(plan) > scorePlan(current)) {
      grouped.set(key, plan);
    }
  });
  return Array.from(grouped.values());
};

const getServicesFromPlan = (plan) => {
  if (Array.isArray(plan.servicesIncluded) && plan.servicesIncluded.length > 0) return plan.servicesIncluded;
  if (typeof plan.services === "string" && plan.services.trim()) {
    return plan.services
      .split(",")
      .map((service) => service.trim())
      .filter(Boolean)
      .map((service) => entitlementLabels[service] || service);
  }
  if (Array.isArray(plan.services) && plan.services.length > 0) {
    return plan.services.map((service) => entitlementLabels[service] || service);
  }
  const entitlements = plan.entitlements;
  if (Array.isArray(entitlements) && entitlements.length > 0) {
    return entitlements.map((service) => entitlementLabels[service] || service);
  }
  if (!entitlements || typeof entitlements !== "object") return [];
  return Object.keys(entitlements)
    .filter((key) => entitlements[key])
    .map((key) => entitlementLabels[key] || key);
};

const toList = (value) => (Array.isArray(value) ? value : []);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const isValidPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 10;
};

const toPayloadPlanId = (planId) => {
  if (typeof planId === "number") return planId;
  const value = String(planId ?? "").trim();
  if (/^\d+$/.test(value)) return Number(value);
  return value;
};

const ClientPurchasesPage = () => {
  const toast = useToast();
  const location = useLocation();
  const { getToken, isAuthenticated, user } = useAuthProvider();

  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentsAcknowledged, setPaymentsAcknowledged] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [form, setForm] = useState({
    companyName: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    domain: "",
    coreService: coreServiceOptions[3],
  });

  const query = new URLSearchParams(location.search);
  const status = query.get("status");
  const requestedPlanId = query.get("planId");

  useEffect(() => {
    if (!isAuthenticated || !getToken) {
      setPaymentsApiTokenProvider(null);
      return;
    }

    setPaymentsApiTokenProvider(() => getToken());
    return () => setPaymentsApiTokenProvider(null);
  }, [getToken, isAuthenticated]);

  useEffect(() => {
    let active = true;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const data = await getPublicSubscriptionPlans();
        const list = Array.isArray(data?.plans) ? data.plans : Array.isArray(data) ? data : [];
        const normalized = list
          .filter(isRecurringPlan)
          .map((plan) => normalizePlan(plan, getFallbackPlan(plan) || {}))
          .filter((entry) => entry.id !== undefined && entry.id !== null);
        const uniquePlans = dedupePlans(normalized);
        if (active && uniquePlans.length > 0) {
          setPlans(uniquePlans);
          setIsLoadingPlans(false);
          return;
        }
      } catch (error) {
        toast({ title: "Using fallback plans", description: error.message, status: "warning" });
      }

      if (active) {
        setPlans(fallbackTiers.map((plan) => normalizePlan(plan)));
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      active = false;
    };
  }, [toast]);

  useEffect(() => {
    if (plans.length === 0 || selectedPlanId) return;
    if (requestedPlanId && plans.some((entry) => String(entry.id) === String(requestedPlanId))) {
      setSelectedPlanId(String(requestedPlanId));
      return;
    }
    const recommended = plans.find((entry) => entry.recommended) || plans[0];
    if (recommended?.id !== undefined && recommended?.id !== null) {
      setSelectedPlanId(String(recommended.id));
    }
  }, [plans, requestedPlanId, selectedPlanId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedCompany = safeParseJson(
      window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE),
      {}
    );

    setForm((prev) => ({
      ...prev,
      companyName: storedCompany?.companyName || prev.companyName,
      customerName: user?.name || prev.customerName,
      customerEmail: user?.email || storedCompany?.billingEmail || prev.customerEmail,
      customerPhone: storedCompany?.billingPhone || prev.customerPhone,
      domain: storedCompany?.primaryDomain || prev.domain,
      coreService: storedCompany?.coreService || prev.coreService,
    }));
  }, [user?.email, user?.name]);

  const selectedPlan = useMemo(
    () => plans.find((entry) => String(entry.id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId]
  );

  const selectedMonthly = Number(selectedPlan?.monthlyPrice);
  const selectedSetup = Number(selectedPlan?.setupFee);
  const dueNowTotal =
    (Number.isFinite(selectedMonthly) ? selectedMonthly : 0) +
    (Number.isFinite(selectedSetup) ? selectedSetup : 0);

  const isEmailValid = isValidEmail(form.customerEmail);
  const isPhoneValid = isValidPhone(form.customerPhone);

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveCompanyProfile = () => {
    if (typeof window === "undefined") return;
    const existing = safeParseJson(
      window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE),
      {}
    );

    const nextProfile = {
      ...existing,
      companyName: form.companyName,
      billingEmail: form.customerEmail,
      billingPhone: form.customerPhone,
      primaryDomain: form.domain,
      coreService: form.coreService,
    };

    window.localStorage.setItem(
      CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE,
      JSON.stringify(nextProfile)
    );
  };

  const handleCheckout = async () => {
    if (isSubmitting) return;
    setCheckoutError("");

    if (!selectedPlan) {
      toast({ title: "Select a plan first", status: "warning" });
      return;
    }

    if (!isEmailValid) {
      toast({ title: "Valid email is required", status: "warning" });
      return;
    }

    if (!isPhoneValid) {
      toast({
        title: "Valid phone number is required",
        description: "Enter at least 10 digits.",
        status: "warning",
      });
      return;
    }

    if (!paymentsAcknowledged) {
      toast({ title: "Please accept payments disclosure", status: "warning" });
      return;
    }

    if (typeof window === "undefined") return;

    setIsSubmitting(true);
    try {
      saveCompanyProfile();

      const origin = window.location.origin;
      const payload = {
        planId: toPayloadPlanId(selectedPlan.id),
        customerEmail: form.customerEmail.trim(),
        customerName: form.customerName.trim(),
        successUrl: `${origin}/client/purchases?status=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/client/purchases?status=cancel`,
      };

      if (form.companyName.trim()) payload.companyName = form.companyName.trim();
      if (form.domain.trim()) payload.domain = form.domain.trim();
      if (form.coreService.trim()) payload.coreService = form.coreService.trim();

      const session = await createClientSubscriptionCheckoutSession(payload);
      const checkoutUrl = session?.url || session?.checkoutUrl || session?.sessionUrl;

      if (!checkoutUrl) {
        throw new Error("Checkout URL missing from response.");
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      const message = error.message || "Unable to start checkout.";
      setCheckoutError(message);
      toast({
        title: "Checkout failed",
        description: message,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={6}>
      <Box>
        <Heading size="md" mb={2}>SaaS Subscription Checkout</Heading>
        <Text color="gray.600" fontSize="sm">
          Standard flow: sign up -> complete contact details (email + phone required) -> choose plan -> Stripe checkout.
        </Text>
      </Box>

      {status ? (
        <Alert status={status === "success" ? "success" : "info"} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            {status === "success"
              ? "Checkout completed. Your subscription purchase has been received."
              : "Checkout canceled. You can retry anytime from this page."}
          </AlertDescription>
        </Alert>
      ) : null}

      {checkoutError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{checkoutError}</AlertDescription>
        </Alert>
      ) : null}

      <Grid templateColumns={{ base: "1fr", xl: "1.7fr 1fr" }} gap={6}>
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {plans.map((plan) => {
              const isSelected = String(plan.id) === String(selectedPlanId);
              const services = getServicesFromPlan(plan);
              const dashboardsIncluded = toList(plan.dashboardsIncluded);
              const platformGuarantees = toList(plan.platformGuarantees);
              const limits = toList(plan.limits);
              return (
                <Box
                  key={String(plan.id)}
                  borderWidth="1px"
                  borderColor={isSelected ? "blue.500" : "gray.200"}
                  borderRadius="md"
                  p={4}
                  bg={isSelected ? "blue.50" : "white"}
                >
                  <Stack spacing={3}>
                    <HStack justify="space-between">
                      <Heading size="sm">{plan.label}</Heading>
                      {plan.recommended ? <Badge colorScheme="blue">Recommended</Badge> : null}
                    </HStack>
                    <Text color="gray.600" fontSize="sm">
                      {plan.marketingDescription || plan.description || "Subscription plan"}
                    </Text>
                    <Text fontWeight="700">
                      {formatCurrency(plan.monthlyPrice, plan.currency)} / month
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Setup fee: {formatCurrency(plan.setupFee, plan.currency)}
                    </Text>
                    {plan.turnaroundDays ? (
                      <Tag size="sm" colorScheme="orange" w="fit-content">
                        <TagLabel>{plan.turnaroundDays} business days turnaround</TagLabel>
                      </Tag>
                    ) : null}
                    <Divider />
                    <Stack spacing={4}>
                      {services.length > 0 ? (
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                            Services Included
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {services.map((service) => (
                              <HStack key={service} spacing={2} align="start">
                                <FiCheck color="#2F855A" />
                                <Text fontSize="sm" color="gray.700">{service}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      ) : null}
                      {dashboardsIncluded.length > 0 ? (
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                            Included Dashboards
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {dashboardsIncluded.map((dashboard) => (
                              <HStack key={dashboard} spacing={2} align="start">
                                <FiCheck color="#2C5282" />
                                <Text fontSize="sm" color="gray.700">{dashboard}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      ) : null}
                      {platformGuarantees.length > 0 ? (
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                            Platform Guarantees
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {platformGuarantees.map((guarantee) => (
                              <HStack key={guarantee} spacing={2} align="start">
                                <FiShield color="#2B6CB0" />
                                <Text fontSize="sm" color="gray.700">{guarantee}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      ) : null}
                      {limits.length > 0 ? (
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                            Limits
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {limits.map((limit) => (
                              <HStack key={limit} spacing={2} align="start">
                                <FiClock color="#805AD5" />
                                <Text fontSize="sm" color="gray.700">{limit}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      ) : null}
                    </Stack>
                    <Button
                      size="sm"
                      colorScheme={isSelected ? "blue" : "gray"}
                      variant={isSelected ? "solid" : "outline"}
                      onClick={() => setSelectedPlanId(String(plan.id))}
                      isDisabled={isLoadingPlans}
                    >
                      {isSelected ? "Selected" : "Select Plan"}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4} bg="white">
          <Stack spacing={4}>
            <Heading size="sm">Checkout Details</Heading>
            <Text color="gray.600" fontSize="sm">
              Contact email and phone are mandatory for SaaS subscription onboarding.
            </Text>

            {selectedPlan ? (
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3} bg="gray.50">
                <Stack spacing={2}>
                  <Text fontWeight="semibold">{selectedPlan.label}</Text>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Monthly subscription</Text>
                    <Text fontWeight="600">{formatCurrency(selectedPlan.monthlyPrice, selectedPlan.currency)}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Setup fee</Text>
                    <Text fontWeight="600">{formatCurrency(selectedPlan.setupFee, selectedPlan.currency)}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Due now</Text>
                    <Text fontWeight="700" color="blue.600">{formatCurrency(dueNowTotal, selectedPlan.currency)}</Text>
                  </HStack>
                </Stack>
              </Box>
            ) : null}

            <FormControl>
              <FormLabel>Company Name</FormLabel>
              <Input value={form.companyName} onChange={(e) => handleInputChange("companyName", e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input value={form.customerName} onChange={(e) => handleInputChange("customerName", e.target.value)} />
            </FormControl>

            <FormControl isRequired isInvalid={Boolean(form.customerEmail) && !isEmailValid}>
              <FormLabel>Contact Email</FormLabel>
              <Input
                type="email"
                value={form.customerEmail}
                onChange={(e) => handleInputChange("customerEmail", e.target.value)}
                placeholder="you@company.com"
              />
            </FormControl>

            <FormControl isRequired isInvalid={Boolean(form.customerPhone) && !isPhoneValid}>
              <FormLabel>Contact Phone</FormLabel>
              <Input
                type="tel"
                value={form.customerPhone}
                onChange={(e) => handleInputChange("customerPhone", e.target.value)}
                placeholder="(555) 123-4567"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Core Service</FormLabel>
              <Select value={form.coreService} onChange={(e) => handleInputChange("coreService", e.target.value)}>
                {coreServiceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Primary Domain</FormLabel>
              <Input
                value={form.domain}
                onChange={(e) => handleInputChange("domain", e.target.value)}
                placeholder="example.com"
              />
            </FormControl>

            <Checkbox
              isChecked={paymentsAcknowledged}
              onChange={(e) => setPaymentsAcknowledged(e.target.checked)}
              colorScheme="blue"
            >
              I understand Telivi bills platform subscription and setup fees, while my customer payments go to my connected Stripe account.
            </Checkbox>

            <Button
              colorScheme="blue"
              onClick={handleCheckout}
              isLoading={isSubmitting}
              loadingText="Opening Stripe..."
              isDisabled={
                isSubmitting ||
                !selectedPlan ||
                !form.customerEmail ||
                !form.customerPhone ||
                !isEmailValid ||
                !isPhoneValid ||
                !paymentsAcknowledged
              }
            >
              Continue to Stripe Checkout
            </Button>
          </Stack>
        </Box>
      </Grid>
    </Stack>
  );
};

export default ClientPurchasesPage;
